import {
  CONFIG,
  DOCS,
  NOTIFICATIONS,
  UNIT,
  USER,
  ROLES
} from '../../utils/constants'
import requestService from '../../services/request'
import enums from '../../utils/enums'
import moment from 'moment-timezone';
const promisesCache = {}

export default {
  state: {
    legalDocuments: [],
    generalDocuments: [],
    ownerDocuments: [],
    syndicDocuments: [],
    syndicManual: null,
    docsLoading: false,
    ownerDocsLoading: false,
    docsError: false
  },
  getters: {
    [DOCS.HAS_LEGAL_DOCS]: state => !!state.legalDocuments.some(document => document.legalDocument && document.legalDocument.status < 3),
    [DOCS.HAS_GENERAL_DOCS]: state => state.generalDocuments.length > 0,
    [DOCS.GET_LEGAL_DOCS]: state => state.legalDocuments,
    [DOCS.GET_GENERAL_DOCS]: state => state.generalDocuments,
    [DOCS.HAS_OWNER_DOCS]: (state) => state.ownerDocuments.length > 0,
    [DOCS.GET_OWNER_DOCS]: state => state.ownerDocuments,
    [DOCS.IS_LOADING]: state => state.docsLoading,
    [DOCS.HAS_ERROR]: state => state.docsError,
    [DOCS.GET_SYNDIC_DOCS]: state => state.syndicDocuments,
    [DOCS.HAS_SYNDIC_DOCS]: (state, getters) => getters[UNIT.GET_IS_SYNDIC] && state.syndicDocuments.length > 0,
  },
  mutations: {
    [DOCS.SET_LEGAL_DOCS]: (state, payload) => {
      state.legalDocuments = payload
    },
    [DOCS.SET_GENERAL_DOCS]: (state, payload) => {
      state.generalDocuments = payload
    },
    [DOCS.SET_OWNER_DOCS]: (state, payload) => {
      state.ownerDocuments = payload
    },
    [DOCS.SET_SYNDIC_DOCS]: (state, payload) => {
      state.syndicDocuments = payload
    }
  },
  actions: {
    async getDocuments({
      commit,
      state,
      dispatch,
      getters
    }, params) {
      if (state.docsLoading) return
      let generalDocs = []
      let legalDocs = []
      try {
        state.docsError = false
        state.docsLoading = true
        const url = `${process.env.GET_BY_ENTERPRISE}/document`
        const paramsKey = JSON.stringify(params)
        if (!promisesCache[paramsKey] || moment().isAfter(promisesCache[paramsKey].validUntil)) {
          promisesCache[paramsKey] = {
            validUntil: moment().add(5, 'minutes').toISOString(),
            promise: requestService(url, 'GET', {
              params,
              localErrorHandling: true
            }).catch(err => {
              delete promisesCache[paramsKey]
              throw err
            }),
          }
        }
        const result = await promisesCache[paramsKey].promise

        if (result) {
          result.sort((a, b) => b.lastModified < a.lastModified ? -1 : 1)
          for (const item of result) {
            if (item.sort === enums.documentsType.general)
              generalDocs.push(item)
            if (item.sort === enums.documentsType.legal)
              legalDocs.push(item)
          }
          legalDocs = [
            ...legalDocs
              .filter(doc => doc.legalDocument.status === 1)
              .sort((a, b) => a.legalDocument.type - b.legalDocument.type),
            ...legalDocs
              .filter(doc => doc.legalDocument.status === 2)
              .sort((a, b) => a.legalDocument.type - b.legalDocument.type),
            ...legalDocs
              .filter(doc => doc.legalDocument.status === 3)
              .sort((a, b) => a.legalDocument.type - b.legalDocument.type)
          ]
          if (getters[USER.GET_USER_ROLE] === ROLES.SYNDIC_ROLE) {
            const productDetails = await dispatch(UNIT.FETCH_PRODUCT_DETAILS)
            const syndicManual = getters[UNIT.SYNDIC_MANUAL_LINK]
            if (productDetails && syndicManual) {
              const syndicManualDoc = {
                "_id": productDetails._id,
                "attachment": syndicManual,
                // "description": "",
                "title": "Manual do Síndico",
              }
              generalDocs.unshift(syndicManualDoc)
            }
          }

        }

      } catch (err) {
        console.error(err)
        state.docsError = true
        legalDocs = []
        generalDocs = []
      } finally {
        commit(DOCS.SET_LEGAL_DOCS, legalDocs)
        commit(DOCS.SET_GENERAL_DOCS, generalDocs)
        commit(NOTIFICATIONS.MUTATE_LEGAL_DOCUMENTS, [...legalDocs])
        commit(NOTIFICATIONS.MUTATE_GENERAL_DOCUMENTS, [...generalDocs])
        state.docsLoading = false
      }
    },
    async getOwnerDocuments({
      commit,
      getters,
      state,
      dispatch
    }) {
      if (state.ownerDocsLoading) {
        return
      }
      let docs = [];
      const isSyndic = getters[UNIT.GET_IS_SYNDIC];
      try {
        state.docsError = false
        state.ownerDocsLoading = true
        const productDetails = await dispatch(UNIT.FETCH_PRODUCT_DETAILS)
        const isProductDetailsEmpty = Object.keys(productDetails).length === 0;

        let pushDocument = ({ type, title, attachment, fileName }) => {
          docs.push({
            _id: `${productDetails._id}_${type}`,
            title: title,
            type: type,
            description: "",
            fileName,
            attachment: attachment,
            lastModified: productDetails?.document?.lastModified
          });
        };

        if (getters[CONFIG.GET_NAVIGATION_RULES].documentCrmEnabled) {
          const url = `${process.env.GET_BY_ENTERPRISE}/getContractDocument`
          const currContract = getters[UNIT.GET_SELECTED].contracts.find(contract => contract.isValid)
          if (currContract) {
            const getContractDocumentParams = {
              contractId: currContract.id
            }
  
            const paramsKey = JSON.stringify(getContractDocumentParams)
            if (!promisesCache[paramsKey] || moment().isAfter(promisesCache[paramsKey].validUntil)) {
              promisesCache[paramsKey] = {
                validUntil: moment().add(5, 'minutes').toISOString(),
                promise: requestService(url, 'GET', {
                  params: getContractDocumentParams,
                  localErrorHandling: true
                }).catch(err => {
                  delete promisesCache[paramsKey]
                  throw err
                }),
              }
            }
            const result = await promisesCache[paramsKey].promise

            if (result) {
              const requestAttachment = await request(url, 'GET', {
                params: { ...getContractDocumentParams, download: true },
                overwriteOptions: {
                  responseType: "arraybuffer",
                },
                localErrorHandling: true
              }).then(res => "data:" + 'application/zip' + ";base64," + Buffer.from(res).toString('base64'))
              pushDocument({
                type: 'pcvContract',
                title: 'Contrato de Compra e Venda',
                fileName: 'Contrato de Compra e Venda.zip',
                attachment: requestAttachment
              })
            }
          }
        }


        if (!isProductDetailsEmpty) {
          if (isSyndic) {
            if (productDetails.links && productDetails.links.syndicLinks && productDetails.links.syndicLinks.length > 0)
              pushDocument(
                {
                  type: 'syndicDocument',
                  title: 'Manual do Síndico',
                  attachment: productDetails.links.syndicLinks[0].syndicManualUrl
                });
          }
          else if (productDetails.document && getters[UNIT.GET_IS_KEY_DELIVERED]) {
            pushDocument({
              type: 'ownerDocument',
              title: 'Manual do Proprietário',
              attachment: productDetails.document.ownerManual
            });
          }
          else
            console.log('Document not found')
        }

      } catch (err) {
        console.error(err)
        state.docsError = true
        docs = []
      } finally {
        commit(isSyndic ? DOCS.SET_SYNDIC_DOCS : DOCS.SET_OWNER_DOCS, docs)
        state.ownerDocsLoading = false;
      }
    }
  },
}



// WEBPACK FOOTER //
// ./src/store/modules/docs.js